import React, { useRef, useState, useEffect } from "react";
import { Download, RotateCcw, Plus, Trash2, Sparkles, Square } from "lucide-react";
import { toast } from "sonner";

const PRESETS = {
  altan: { name: "Altan 4x3 m", shapes: [
    { id: "altan", type: "rect", x: 80, y: 80, w: 240, h: 180, fill: "#f5f5f4", stroke: "#1d4ed8", label: "Altan 4x3m" },
    { id: "house", type: "rect", x: 80, y: 30, w: 240, h: 50, fill: "#fafafa", stroke: "#71717a", label: "Husvägg" },
  ]},
  garage: { name: "Garage 6x6 m", shapes: [
    { id: "garage", type: "rect", x: 60, y: 60, w: 240, h: 240, fill: "#f5f5f4", stroke: "#1d4ed8", label: "Garage 6x6m" },
    { id: "door", type: "rect", x: 120, y: 295, w: 120, h: 8, fill: "#fbbf24", stroke: "#92400e", label: "Garageport" },
  ]},
  attefall: { name: "Attefallshus 5x6 m", shapes: [
    { id: "house", type: "rect", x: 60, y: 60, w: 200, h: 240, fill: "#f5f5f4", stroke: "#1d4ed8", label: "Attefallshus 5x6m" },
  ]},
};

export default function DrawingCreate() {
  const svgRef = useRef(null);
  const [shapes, setShapes] = useState(PRESETS.altan.shapes);
  const [selectedId, setSelectedId] = useState(null);
  const [scaleLabel, setScaleLabel] = useState("Skala 1:100");
  const dragRef = useRef(null);

  const loadPreset = (key) => {
    setShapes(JSON.parse(JSON.stringify(PRESETS[key].shapes)));
    setSelectedId(null);
    toast.success(`Laddade: ${PRESETS[key].name}`);
  };

  const addShape = () => {
    const id = `shape_${Date.now()}`;
    setShapes((s) => [...s, { id, type: "rect", x: 100, y: 100, w: 80, h: 60, fill: "#f5f5f4", stroke: "#1d4ed8", label: "Ny form" }]);
    setSelectedId(id);
  };

  const removeSelected = () => {
    if (!selectedId) return;
    setShapes((s) => s.filter((sh) => sh.id !== selectedId));
    setSelectedId(null);
  };

  const onMouseDown = (e, id) => {
    setSelectedId(id);
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const start = pt.matrixTransform(svg.getScreenCTM().inverse());
    const sh = shapes.find((s) => s.id === id);
    dragRef.current = { id, dx: start.x - sh.x, dy: start.y - sh.y };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const svg = svgRef.current;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const p = pt.matrixTransform(svg.getScreenCTM().inverse());
      setShapes((s) => s.map((sh) => sh.id === dragRef.current.id
        ? { ...sh, x: Math.max(0, Math.min(400 - sh.w, p.x - dragRef.current.dx)), y: Math.max(0, Math.min(400 - sh.h, p.y - dragRef.current.dy)) }
        : sh
      ));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const updateSelected = (key, val) => {
    if (!selectedId) return;
    setShapes((s) => s.map((sh) => sh.id === selectedId ? { ...sh, [key]: val } : sh));
  };

  const exportSvg = () => {
    const svg = svgRef.current;
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "ritning.svg"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Ritning sparad som SVG");
  };

  const selected = shapes.find((s) => s.id === selectedId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <Sparkles className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Bygglovsguide</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Ritningsskapare</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600 max-w-2xl">Skapa enkla planritningar för altan, garage eller attefallshus. Dra rektanglar och exportera som SVG.</p>

      <div className="mt-8 grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-3">
          <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">Mallar</div>
            <div className="space-y-1">
              {Object.entries(PRESETS).map(([k, p]) => (
                <button
                  key={k}
                  data-testid={`preset-${k}`}
                  onClick={() => loadPreset(k)}
                  className="w-full text-left px-3 py-2 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50"
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-2">
            <button onClick={addShape} data-testid="add-shape"
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-sm text-sm font-medium">
              <Plus className="w-4 h-4" /> Lägg till rektangel
            </button>
            <button onClick={removeSelected} disabled={!selectedId} data-testid="remove-shape"
              className="w-full inline-flex items-center justify-center gap-2 border border-zinc-300 text-zinc-700 px-3 py-2 rounded-sm text-sm font-medium hover:bg-zinc-100 disabled:opacity-50">
              <Trash2 className="w-4 h-4" /> Ta bort markerad
            </button>
            <button onClick={() => loadPreset("altan")} data-testid="reset"
              className="w-full inline-flex items-center justify-center gap-2 border border-zinc-300 text-zinc-700 px-3 py-2 rounded-sm text-sm font-medium hover:bg-zinc-100">
              <RotateCcw className="w-4 h-4" /> Återställ
            </button>
            <button onClick={exportSvg} data-testid="export-svg"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-sm text-sm font-medium">
              <Download className="w-4 h-4" /> Exportera SVG
            </button>
          </div>

          {selected && (
            <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Markerad form</div>
              <div>
                <label className="text-xs text-zinc-600">Etikett</label>
                <input value={selected.label} onChange={(e) => updateSelected("label", e.target.value)} data-testid="shape-label"
                  className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-zinc-600">Bredd</label>
                  <input type="number" value={selected.w} onChange={(e) => updateSelected("w", Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded-sm" />
                </div>
                <div>
                  <label className="text-xs text-zinc-600">Höjd</label>
                  <input type="number" value={selected.h} onChange={(e) => updateSelected("h", Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded-sm" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <input value={scaleLabel} onChange={(e) => setScaleLabel(e.target.value)} className="text-sm font-medium px-2 py-1 border border-transparent hover:border-zinc-200 rounded-sm" />
            <div className="text-xs text-zinc-500">Klicka för att markera · dra för att flytta</div>
          </div>
          <svg ref={svgRef} viewBox="0 0 400 400" className="w-full aspect-square border border-zinc-200 bg-zinc-50">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e4e4e7" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="400" height="400" fill="url(#grid)" />
            {shapes.map((s) => (
              <g key={s.id}
                 onMouseDown={(e) => onMouseDown(e, s.id)}
                 style={{ cursor: "move" }}
                 data-testid={`svg-shape-${s.id}`}>
                <rect
                  x={s.x} y={s.y} width={s.w} height={s.h}
                  fill={s.fill} stroke={selectedId === s.id ? "#2563eb" : s.stroke}
                  strokeWidth={selectedId === s.id ? 2.5 : 1.5}
                />
                <text x={s.x + s.w / 2} y={s.y + s.h / 2} textAnchor="middle" dominantBaseline="middle"
                  fontSize="10" fill="#27272a" pointerEvents="none">
                  {s.label}
                </text>
              </g>
            ))}
            <text x="10" y="395" fontSize="9" fill="#71717a">{scaleLabel}</text>
          </svg>
          <p className="mt-3 text-xs text-zinc-500">
            * Detta är en förenklad ritningsskapare för översiktliga planer. För skarpa bygglovsritningar rekommenderas CAD-program.
          </p>
        </div>
      </div>
    </div>
  );
}
